import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo } from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useDocumentCapabilities } from "../../auth/permissions.js";
import { PageHeader } from "../../components/PageHeader.js";
import { PageLoader } from "@/components/ui/page-loader";
import { listAllDocs, listCategories } from "./api.js";
import { buildTree, type CategoryLike } from "./doc-sidebar/tree.js";
import type { DocSummary } from "@xop/dashboard-shared";

/**
 * Default landing for `/documentation`.
 *
 * Auto-drills into real documents so the operator lands on a list instead
 * of an empty index page. The always-on sidebar (DocumentationLayout)
 * already shows the full tree; the right pane should be useful from frame
 * one.
 *
 * Routing contract (the documentation is the library shelf, 2026-09-14;
 * release documentation is read under its version on CRM ▸ Releases and
 * never lands here):
 *   - a library folder holds documents → the busiest folder's page
 *   - folders exist but hold nothing → the first folder's page
 *   - no folders, but unfiled docs → the Uncategorized bucket
 *   - nothing at all → the empty state below, nudging the operator to
 *     create something.
 *
 * Loading state: render a skeleton instead of redirecting on stale
 * cache — otherwise the user briefly sees the wrong page.
 */
export function DocumentationIndex(): JSX.Element {
  // "+ New document" is open to the create tier; "Manage categories" (folder
  // management) stays edit-tier.
  const { canCreate, canEditAll } = useDocumentCapabilities();

  const { data: cats, isLoading: catsLoading } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });

  // The same query keys the sidebar populates, so landing here costs one
  // fetch between the two rather than two.
  const { data: allDocs, isLoading: docsLoading } = useQuery({
    queryKey: ["docs", "tree", "all"],
    queryFn: () => listAllDocs({ q: "", category_id: "all" }),
  });
  const target = useMemo<string | null>(() => {
    return resolveFirstTarget(cats?.data ?? [], allDocs ?? []);
  }, [cats, allDocs]);

  if (catsLoading || docsLoading) {
    return <PageLoader />;
  }

  if (target) return <Navigate to={target} replace />;

  // Empty state: render the create-action prompt right where the docs
  // would otherwise be. Sidebar already shows there's nothing in the
  // tree.
  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <PageHeader
        title="Documentation"
        subtitle="Browse by folder. Release guides live under their version on CRM ▸ Releases."
        right={
          canCreate ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {canEditAll && (
                <Button component={RouterLink} to="/documentation/categories" variant="text" color="inherit">
                  Manage categories
                </Button>
              )}
              <Button component={RouterLink} to="/documentation/manage/new" variant="contained">
                + New document
              </Button>
            </Box>
          ) : undefined
        }
      />
      <Box
        sx={{
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
          p: 5,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>No documents yet.</Typography>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
          {canEditAll
            ? 'Add a category from "Manage categories", then upload, write, or AI-generate the first document.'
            : canCreate
              ? "Upload, write, or AI-generate the first document."
              : "Ask an admin to add the first category and document."}
        </Typography>
      </Box>
    </Box>
  );
}

/** The deepest landing that is guaranteed to render something. */
export function resolveFirstTarget(cats: CategoryLike[], docs: DocSummary[]): string | null {
  const tree = buildTree(cats, docs);

  // Sub-folders count too: a parent that only holds sub-folders lands on
  // its busiest sub-folder rather than on an empty page.
  const busiest = tree.library
    .flatMap((c) => [c, ...c.children])
    .find((c) => c.count > 0 && c.id != null);
  if (busiest) return `/documentation/type/${encodeURIComponent(busiest.slug)}`;

  const first = cats[0];
  if (first) return `/documentation/type/${encodeURIComponent(first.slug)}`;

  // No folders. Unfiled docs still have a page.
  if (docs.length > 0) return "/documentation/type/uncategorized";
  return null;
}
